import React, { useEffect, useState, useMemo } from "react";
import { Form, Input, Typography, Drawer, message, Select, Tooltip, Button, Space } from "antd";
import { UserOutlined, CopyOutlined, InfoCircleOutlined, PaperClipOutlined, PlusCircleOutlined, SendOutlined, PlusOutlined } from "@ant-design/icons";
import RecipientSelector from "./RecipientSelector";
import AttachmentUpload from "./AttachmentUpload";
import AttachmentList, { getAttachmentObjectKey } from "./AttachmentList";
import GroupManagementDrawer from "../../../features/masterData/EmailGroup/components/GroupManagementDrawer";
import { useAuth } from "../../../hooks/useAuth";

const { Text, Link } = Typography;

/**
 * 重发模式：把 "a@x.com, b@x.com; c@x.com" 拆成去重后的邮箱数组
 * 跟 GeneralEmailEditor 中的实现保持一致（避免循环依赖，重复定义一次）
 */
const splitRecipientString = (raw) => {
    if (!raw || typeof raw !== "string") return [];
    return [
        ...new Set(
            raw
                .split(/[,;]/)
                .map((s) => s.trim())
                .filter(Boolean),
        ),
    ];
};

/**
 * 适配 userEmailConfig 到统一的发件人对象结构
 *
 * userEmailConfig 结构: { email_address, display_name, config_name }
 * 预设账号结构: { key, email, display_name, description }
 *
 * ⚠️ 注意：key 必须为 null 才能与 EmailEditorForm 中 Select 的
 *    "个人邮箱"选项 (value={null}) 正确匹配。
 *    如果设置为其他值（如 'user'），Select 会显示原始 key 值。
 *
 * @param {Object} userConfig - 用户邮箱配置
 * @returns {Object|null} 适配后的发件人对象
 */
export const adaptUserEmailConfig = (userConfig) => {
    if (!userConfig) return null;
    return {
        key: null, // ⚠️ 必须为 null，匹配 Select.Option value={null}
        email: userConfig.email_address,
        display_name: userConfig.display_name || userConfig.config_name,
        description: '个人绑定邮箱',
    };
};

/**
 * 邮件表单组件 - 包含发件人、收件人、抄送、主题、附件等字段
 */
const EmailEditorForm = ({
    form,
    templateRecipients,
    attachments = [],
    onAttachmentsChange,
    // 发件人相关 props
    senderOptions = [], // 预设发件账号列表 [{ key, email, display_name, description }]
    selectedSender = null, // 当前选中的发件人
    senderEditable = true, // 是否允许切换发件人
    onSenderChange = null, // 发件人选择变化回调
    // 发件箱邮件重发：种子（T07 / FR-003 / FR-014 / C2 / T12）
    //   - 非空时进入重发模式，TO/CC 输入框上方根据 source_is_bulk_email 显示"沿用原 TO/CC"按钮 或 群发提示文本
    //   - source_status === 'Failed' 时按钮文案改为"沿用原失败时的收件人"（T12 / S5.2 配套）
    resendDraftSeed = null,
}) => {
    const isResendMode = resendDraftSeed !== null && resendDraftSeed !== undefined;
    const isResendBulk = isResendMode && resendDraftSeed?.source_is_bulk_email === true;
    const isResendFailed = isResendMode && resendDraftSeed?.source_status === "Failed";

    // 原邮件 TO/CC 拆分后的去重列表（"沿用原"按钮的数据源）
    const originalTo = useMemo(() => splitRecipientString(resendDraftSeed?.source_to_address), [resendDraftSeed]);
    const originalCc = useMemo(() => splitRecipientString(resendDraftSeed?.source_cc_address), [resendDraftSeed]);

    // 沿用原 TO / CC：把原邮件的收件人追加到当前输入框（去重）
    const handleReuseOriginal = (field, originalList) => {
        if (!originalList || originalList.length === 0) {
            message.info(`原邮件没有${field === "to" ? "收件人" : "抄送"}可沿用`);
            return;
        }
        const current = form.getFieldValue(field) || [];
        const merged = [...new Set([...current, ...originalList])];
        form.setFieldsValue({ [field]: merged });
        message.success(`已沿用原邮件 ${originalList.length} 个${field === "to" ? "收件人" : "抄送"}`);
    };

    // "沿用原 TO" / "沿用原 CC" 按钮文案——失败邮件按 T12 / S5.2 改为"沿用原失败时的收件人"
    const reuseToLabel = isResendFailed
        ? `+ 沿用原失败时的收件人（${originalTo.length} 人）`
        : `+ 沿用原 TO（${originalTo.length} 人）`;
    const reuseCcLabel = isResendFailed
        ? `+ 沿用原失败时的抄送（${originalCc.length} 人）`
        : `+ 沿用原 CC（${originalCc.length} 人）`;
    // 从全局上下文获取用户邮箱配置
    const { userEmailConfig } = useAuth();
    // 创建分组抽屉状态
    const [createGroupDrawerVisible, setCreateGroupDrawerVisible] = useState(false);
    // 用于强制刷新选择器数据的key
    const [dataRefreshKey, setDataRefreshKey] = useState(0);

    // 处理附件添加
    const handleAttachmentAdd = (newAttachment) => {
        const updatedAttachments = [...attachments, newAttachment];
        onAttachmentsChange(updatedAttachments);
    };

    // 处理附件删除
    // 按 object_key/storage_path 定位；两者都缺失时退回 filename，
    // 避免多个键为 undefined 的附件在一次删除中被全部误删
    const handleAttachmentRemove = (removedAttachment) => {
        const removedKey = getAttachmentObjectKey(removedAttachment) || removedAttachment.filename;
        const updatedAttachments = attachments.filter((att) => (getAttachmentObjectKey(att) || att.filename) !== removedKey);
        onAttachmentsChange(updatedAttachments);
    };

    // 处理附件重命名
    const handleAttachmentRename = (attachment, newName) => {
        const targetKey = getAttachmentObjectKey(attachment) || attachment.filename;
        const updatedAttachments = attachments.map((att) => {
            if ((getAttachmentObjectKey(att) || att.filename) === targetKey) {
                return { ...att, filename: newName };
            }
            return att;
        });

        onAttachmentsChange(updatedAttachments);
    };
    // 从模板收件人配置中提取分组和地址
    const extractRecipientsFromConfig = (config) => {
        if (!config) return [];

        const recipients = [];

        // 添加分组
        if (config.groups && Array.isArray(config.groups)) {
            recipients.push(...config.groups);
        }

        // 添加地址
        if (config.addresses && Array.isArray(config.addresses)) {
            recipients.push(...config.addresses);
        }

        return recipients;
    };

    // 从模板加载默认收件人
    useEffect(() => {
        // 发件箱邮件重发：FR-003 重发模式下 TO/CC 必须由用户显式输入或点"沿用原"按钮才能填入
        // 不允许 templateRecipients 在重发场景下自动覆盖父级已经设成空数组的 form.to/cc
        if (isResendMode) {
            console.log("[EmailEditorForm] 重发模式：跳过模板默认收件人填充（FR-003）");
            return;
        }

        if (templateRecipients) {
            console.log("[EmailEditorForm] 收到模板收件人配置:", templateRecipients);

            // 获取当前表单值
            const currentValues = form.getFieldsValue();

            const updates = {};

            // 处理收件人 - 提取分组和地址
            if (!currentValues.to || currentValues.to.length === 0) {
                const toRecipients = extractRecipientsFromConfig(templateRecipients.default_to);
                if (toRecipients.length > 0) {
                    updates.to = toRecipients;
                }
            }

            // 处理抄送 - 提取分组和地址
            if (!currentValues.cc || currentValues.cc.length === 0) {
                const ccRecipients = extractRecipientsFromConfig(templateRecipients.default_cc);
                if (ccRecipients.length > 0) {
                    updates.cc = ccRecipients;
                }
            }

            if (Object.keys(updates).length > 0) {
                form.setFieldsValue(updates);
            } else {
            }
        } else {
            console.log("[EmailEditorForm] 未收到模板收件人配置");
        }
    }, [templateRecipients, form, isResendMode]);

    // 处理创建分组成功
    const handleCreateGroupSuccess = () => {
        // 分组创建成功后，刷新RecipientSelector的数据
        setDataRefreshKey((prev) => prev + 1);
        message.success("分组数据已刷新");
    };

    return (
        <>
            <div className="email-header-fields">
                {/* 发件人 */}
                <div className="email-field-row">
                    <div className="field-label">
                        <Text strong>
                            <SendOutlined /> 发件人：
                        </Text>
                    </div>
                    <div className="field-input">
                        {senderEditable && senderOptions.length > 0 ? (
                            // 可编辑且有预设账号时显示下拉选择器
                            <Select
                                value={selectedSender?.key || null}
                                onChange={(value) => {
                                    if (value === null) {
                                        // 选择"使用个人邮箱"
                                        onSenderChange?.(null);
                                    } else {
                                        const sender = senderOptions.find((s) => s.key === value);
                                        onSenderChange?.(sender);
                                    }
                                }}
                                disabled={!senderEditable}
                                style={{ width: "100%" }}
                                placeholder="选择发件账号"
                            >
                                <Select.Option value={null}>
                                    <span>
                                        {userEmailConfig?.email_address || "个人绑定邮箱"}
                                        <span style={{ color: "#999", marginLeft: 8 }}>&lt;{userEmailConfig?.email_address}&gt;</span>
                                    </span>
                                </Select.Option>
                                {senderOptions.map((sender) => (
                                    <Select.Option key={sender.key} value={sender.key}>
                                        <Tooltip title={sender.description} placement="right">
                                            <span>
                                                {sender.display_name || sender.email}
                                                <span style={{ color: "#999", marginLeft: 8 }}>
                                                    &lt;{sender.email}&gt;
                                                </span>
                                            </span>
                                        </Tooltip>
                                    </Select.Option>
                                ))}
                            </Select>
                        ) : (
                            // 禁止编辑或无预设账号时显示文本
                            <Text type="secondary" style={{ lineHeight: "32px" }}>
                                {selectedSender ? (
                                    // 选中了预设账号
                                    <span>
                                        {selectedSender.display_name || selectedSender.email}
                                        <span style={{ color: "#999", marginLeft: 8 }}>
                                            &lt;{selectedSender.email}&gt;
                                        </span>
                                    </span>
                                ) : userEmailConfig ? (
                                    // 显示用户绑定的邮箱
                                    <span>
                                        {userEmailConfig.display_name || userEmailConfig.config_name}
                                        <span style={{ color: "#999", marginLeft: 8 }}>
                                            &lt;{userEmailConfig.email_address}&gt;
                                        </span>
                                    </span>
                                ) : (
                                    // 用户未配置邮箱
                                    <Text type="warning">未配置邮箱，请先在"邮箱配置"中添加</Text>
                                )}
                            </Text>
                        )}
                    </div>
                </div>

                {/* ====== 重发模式：收件人区上方的快捷按钮 / 群发提示文本（T07.b / FR-014 / C2） ====== */}
                {isResendMode && (
                    <div className="email-field-row" style={{ marginBottom: 4 }}>
                        <div className="field-label" />
                        <div className="field-input">
                            {isResendBulk ? (
                                // C2 / FR-014：群发邮件不显示"沿用原 TO"快捷按钮，改为 secondary text 提示
                                <Text type="secondary" style={{ fontSize: 12 }}>
                                    原邮件是群发单显模式，共发给 {originalTo.length || "?"} 个收件人；本次重发请输入要补发的收件人。
                                </Text>
                            ) : (
                                <Space size={8} wrap>
                                    {originalTo.length > 0 && (
                                        <Button size="small" type="dashed" icon={<PlusOutlined />} onClick={() => handleReuseOriginal("to", originalTo)}>
                                            {reuseToLabel}
                                        </Button>
                                    )}
                                    {originalCc.length > 0 && (
                                        <Button size="small" type="dashed" icon={<PlusOutlined />} onClick={() => handleReuseOriginal("cc", originalCc)}>
                                            {reuseCcLabel}
                                        </Button>
                                    )}
                                    {originalTo.length === 0 && originalCc.length === 0 && (
                                        <Text type="secondary" style={{ fontSize: 12 }}>
                                            原邮件没有可沿用的收件人，请手动输入。
                                        </Text>
                                    )}
                                </Space>
                            )}
                        </div>
                        <div className="field-extra" />
                    </div>
                )}

                {/* 收件人 */}
                <div className="email-field-row">
                    <div className="field-label">
                        <Text strong>
                            <UserOutlined /> 收件人：
                        </Text>
                    </div>
                    <div className="field-input">
                        <Form.Item name="to" rules={[{ required: true, message: "请输入收件人" }]} className="inline-form-item">
                            <RecipientSelector placeholder="选择邮箱分组或输入邮箱地址" allowGroups={true} allowCustomInput={true} allowClear refreshKey={dataRefreshKey} />
                        </Form.Item>
                    </div>
                    <div className="field-extra">
                        <Link onClick={() => setCreateGroupDrawerVisible(true)} style={{ fontSize: 12, whiteSpace: "nowrap" }}>
                            <PlusCircleOutlined /> 新建分组
                        </Link>
                    </div>
                </div>

                {/* 抄送 */}
                <div className="email-field-row">
                    <div className="field-label">
                        <Text strong>
                            <CopyOutlined /> 抄送：
                        </Text>
                    </div>
                    <div className="field-input">
                        <Form.Item name="cc" className="inline-form-item">
                            <RecipientSelector placeholder="选择邮箱分组或输入抄送邮箱地址" allowGroups={true} allowCustomInput={true} allowClear refreshKey={dataRefreshKey} />
                        </Form.Item>
                    </div>
                </div>

                {/* 主题 */}
                <div className="email-field-row">
                    <div className="field-label">
                        <Text strong>
                            <InfoCircleOutlined /> 主题：
                        </Text>
                    </div>
                    <div className="field-input">
                        <Form.Item name="subject" rules={[{ required: true, message: "请输入邮件主题" }]} className="inline-form-item">
                            <Input placeholder="请输入邮件主题" allowClear />
                        </Form.Item>
                    </div>
                </div>

                {/* 附件 */}
                <div className="email-field-row">
                    <div className="field-label">
                        <Text strong>
                            <PaperClipOutlined /> 附件：
                        </Text>
                    </div>
                    <div className="field-input">
                        <div className="attachment-section">
                            <AttachmentUpload onAttachmentAdd={handleAttachmentAdd} />
                            {attachments.length > 0 && <AttachmentList attachments={attachments} onAttachmentRemove={handleAttachmentRemove} onAttachmentRename={handleAttachmentRename} />}
                        </div>
                    </div>
                </div>
            </div>

            {/* 创建邮件分组抽屉 */}
            <Drawer title="新建邮件分组" placement="right" width={800} open={createGroupDrawerVisible} onClose={() => setCreateGroupDrawerVisible(false)} destroyOnClose>
                <GroupManagementDrawer mode="create" group={null} onSuccess={handleCreateGroupSuccess} onCancel={() => setCreateGroupDrawerVisible(false)} />
            </Drawer>
        </>
    );
};

export default EmailEditorForm;
